// === Admin: Chemicals ===
// Full CRUD — list, add, edit, soft-delete.
// Ported from Click2Gro PHP (ChemicalsController.php / chemical table).
// Joins: categories, fertilzer_companies. Complex fields: crop_type (array),
// plant_nutrients (dict), chemical_label + safety_data_sheet_link (URL/file).

import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Pagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Chip,
  Grid,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ArrowBack as BackIcon,
  Description as DescriptionIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { alpha, darken } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useSdsDocuments, SdsDocumentViewerDialog } from '@click2gro/ui';
import { useAdminApi } from '../lib/use-admin-api';
import { useAuth } from '../lib/auth';
import {
  AppTableHead,
  AppTableHeadCell,
  AppTableRow,
  ROWS_PER_PAGE_OPTIONS,
} from '../components/AppTable';
import {
  CatalogueCountrySwitch,
  catalogueCountryName,
  useCatalogueCountry,
  toCatalogueCountry,
  catalogueCurrency,
  type CatalogueCountry,
} from '../components/CatalogueCountrySwitch';
import { useCountries } from '../lib/countries';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PlantNutrient {
  readonly id: number;
  readonly element: string;
  readonly symbol: string;
  readonly order: number;
  readonly background_color: string;
  readonly text_color: string;
}

interface CategoryOption    { readonly id: number; readonly category: string; }
interface CompanyOption     { readonly id: number; readonly company: string; }
interface CropTypeOption    { readonly id: number; readonly crop_type: string; }

type LinkStatus = 'active' | 'issue' | null;

interface ChemicalRow {
  readonly id: number;
  readonly product: string | null;
  readonly category: string | null;  // VARCHAR field, not a FK
  readonly fertilzer_company: string | null;  // VARCHAR field, not a FK
  readonly rate: string | null;
  readonly cost: number | null;
  readonly withhold_days: number | null;
  readonly safety_data_sheet_link: string | null;
  readonly chemical_label: string | null;
  readonly safety_data_sheets: number | null;
  // signed URLs resolved by API (for stored filenames)
  readonly chemical_label_url: string | null;
  readonly safety_data_sheet_link_url: string | null;
  readonly crop_type: readonly number[] | null;
  readonly plant_nutrients: Record<string, string> | null;
  readonly country_code?: string | null;
  readonly mapp_number?: string | null;
  // link-check columns (populated by nightly cron + recheck endpoint)
  readonly sds_link_status: LinkStatus;
  readonly sds_link_last_checked_at: string | null;
  readonly sds_link_status_message: string | null;
  readonly sds_link_replacement_url: string | null;
  readonly label_link_status: LinkStatus;
  readonly label_link_last_checked_at: string | null;
  readonly label_link_status_message: string | null;
  readonly label_link_replacement_url: string | null;
  readonly created_at: string | null;
  readonly created_by: string | null;
  readonly updated_at: string | null;
  readonly updated_by: string | null;
}

interface FormState {
  product: string;
  category: string | '';
  fertilzer_company: string | '';
  rate: string;
  cost: string;
  withhold_days: string;
  safety_data_sheet_link: string;
  chemical_label: string;
  safety_data_sheets: number | '';
  // signed view URLs for stored files (edit mode only)
  viewUrl_sds: string | null;
  viewUrl_chem: string | null;
  // link-check status (edit mode only — surfaced as banner in dialog)
  sds_link_status: LinkStatus;
  sds_link_status_message: string | null;
  sds_link_replacement_url: string | null;
  sds_link_last_checked_at: string | null;
  label_link_status: LinkStatus;
  label_link_status_message: string | null;
  label_link_replacement_url: string | null;
  label_link_last_checked_at: string | null;
  crop_type_ids: readonly number[];
  plant_nutrients: Record<string, string>;
  country_code: CatalogueCountry;
  mapp_number: string;
  created_by: string;
  updated_by: string;
}

const DEFAULT_FORM: FormState = {
  product: '',
  category: '',
  fertilzer_company: '',
  rate: '',
  cost: '',
  withhold_days: '',
  safety_data_sheet_link: '',
  chemical_label: '',
  safety_data_sheets: '',
  viewUrl_sds: null,
  viewUrl_chem: null,
  sds_link_status: null,
  sds_link_status_message: null,
  sds_link_replacement_url: null,
  sds_link_last_checked_at: null,
  label_link_status: null,
  label_link_status_message: null,
  label_link_replacement_url: null,
  label_link_last_checked_at: null,
  crop_type_ids: [],
  plant_nutrients: {},
  country_code: 'AU',
  mapp_number: '',
  created_by: '',
  updated_by: '',
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtAuDate(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  return new Intl.DateTimeFormat('en-AU', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).format(d);
}

// ---------------------------------------------------------------------------
// LinkStatusChip — list-view "Link check" column. Renders Active/Issue chip
// only for live external URLs; bucket files render as "—".
// ---------------------------------------------------------------------------

interface LinkStatusChipProps {
  label: string;
  status: LinkStatus;
  url: string | null;
  message: string | null;
}

function LinkStatusChip({ label, status, url, message }: LinkStatusChipProps) {
  // Hide entirely for bucket-stored files (no http prefix) or empty values —
  // those don't rot, so there's nothing to flag.
  const isExternal = Boolean(url) && (url!.startsWith('http://') || url!.startsWith('https://'));
  if (!isExternal) return null;
  if (status === 'active') {
    return <Chip label={`${label}: Active`} size="small" color="success" sx={{ fontSize: 11 }} />;
  }
  if (status === 'issue') {
    return (
      <Tooltip title={message ?? 'Broken link — see edit dialog for details'}>
        <Chip label={`${label}: Issue`} size="small" color="error" sx={{ fontSize: 11 }} />
      </Tooltip>
    );
  }
  // External URL but never checked yet
  return <Chip label={`${label}: Pending`} size="small" variant="outlined" sx={{ fontSize: 11 }} />;
}

// ---------------------------------------------------------------------------
// LinkCheckBanner — shown above DocField when the URL is broken or has a
// suggested replacement. Renders Accept / Reject / Recheck actions.
// ---------------------------------------------------------------------------

interface LinkCheckBannerProps {
  status: LinkStatus;
  message: string | null;
  replacementUrl: string | null;
  lastCheckedAt: string | null;
  onAccept: () => Promise<void> | void;
  onReject: () => Promise<void> | void;
  onRecheck: () => Promise<void> | void;
  busy: boolean;
}

function LinkCheckBanner({
  status, message, replacementUrl, lastCheckedAt,
  onAccept, onReject, onRecheck, busy,
}: LinkCheckBannerProps) {
  if (status !== 'issue') return null;
  return (
    <Alert
      severity="warning"
      sx={{ mb: 1 }}
      action={
        <Button size="small" color="inherit" onClick={() => void onRecheck()} disabled={busy}>
          Recheck
        </Button>
      }
    >
      <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
        Link check failed: {message ?? 'unknown reason'}
        {lastCheckedAt && (
          <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontWeight: 400, ml: 1 }}>
            (last checked {fmtAuDate(lastCheckedAt)})
          </Typography>
        )}
      </Typography>
      {replacementUrl ? (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
            Suggested replacement:&nbsp;
            <a href={replacementUrl} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>
              {replacementUrl}
            </a>
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
            <Button size="small" variant="contained" color="success" onClick={() => void onAccept()} disabled={busy}>
              Accept replacement
            </Button>
            <Button size="small" variant="outlined" color="inherit" onClick={() => void onReject()} disabled={busy}>
              Reject
            </Button>
          </Box>
        </Box>
      ) : (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
          No replacement suggestion was found. Paste a new URL above and Save, or click Recheck.
        </Typography>
      )}
    </Alert>
  );
}

// ---------------------------------------------------------------------------
// DocField — URL input + Link / View buttons (inline, mirrors safety-data-sheets)
// ---------------------------------------------------------------------------

interface DocFieldProps {
  label: string;
  urlValue: string;
  viewUrl?: string | null;
  onUrlChange: (v: string) => void;
  onOpen: (url: string, title: string) => void;
}

function DocField({ label, urlValue, viewUrl, onUrlChange, onOpen }: DocFieldProps) {
  const isUrl  = urlValue.startsWith('http://') || urlValue.startsWith('https://');
  const isFile = Boolean(urlValue) && !isUrl;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="https://…"
          value={urlValue}
          onChange={(e) => onUrlChange(e.target.value)}
          sx={isFile ? { '& .MuiOutlinedInput-root': { bgcolor: (t) => t.palette.action.hover } } : {}}
        />
        <Tooltip title={isUrl ? 'Open link' : isFile ? 'Stored file – use View' : 'Enter URL first'}>
          <span>
            <Button
              size="small"
              variant="contained"
              color={isUrl ? 'success' : 'inherit'}
              disabled={!isUrl}
              onClick={() => { if (isUrl) onOpen(urlValue, label); }}
              sx={{ minWidth: 60, whiteSpace: 'nowrap', flexShrink: 0 }}
            >Link</Button>
          </span>
        </Tooltip>
        {/* View resolves from the single source of truth (safety_data_sheets FK),
            so it appears whenever the API returns a signed URL — even when this
            chemical's own link columns are empty (SDS-linked products). */}
        {viewUrl && (
          <Tooltip title="View document">
            <span>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={() => onOpen(viewUrl, label)}
                sx={{ minWidth: 60, whiteSpace: 'nowrap', flexShrink: 0 }}
              >View</Button>
            </span>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// CropTypeSelector — searchable checkbox list
// ---------------------------------------------------------------------------

interface CropTypeSelectorProps {
  cropTypes: readonly CropTypeOption[];
  selectedIds: readonly number[];
  onChange: (ids: readonly number[]) => void;
}

function CropTypeSelector({ cropTypes, selectedIds, onChange }: CropTypeSelectorProps) {
  const [q, setQ] = useState('');
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const filtered    = useMemo(
    () => cropTypes.filter((ct) => ct.crop_type.toLowerCase().includes(q.toLowerCase())),
    [cropTypes, q],
  );
  const toggle = (id: number) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(Array.from(next));
  };
  const selectAll  = () => onChange(filtered.map((ct) => ct.id));
  const clearAll   = () => {
    const filteredIds = new Set(filtered.map((ct) => ct.id));
    onChange(Array.from(selectedSet).filter((id) => !filteredIds.has(id)));
  };

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.5, display: 'block' }}>
        Crop Types ({selectedIds.length} selected)
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <TextField
          size="small"
          placeholder="Search crop types…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ flexGrow: 1 }}
        />
        <Button size="small" variant="outlined" onClick={selectAll}>All</Button>
        <Button size="small" variant="outlined" color="inherit" onClick={clearAll}>Clear</Button>
      </Box>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          maxHeight: 300,
          overflowY: 'auto',
          p: 1,
        }}
      >
        <Grid container columns={2} spacing={0}>
          {filtered.map((ct) => (
            <Grid key={ct.id} item xs={1}>
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={selectedSet.has(ct.id)}
                    onChange={() => toggle(ct.id)}
                    sx={{ p: 0.5 }}
                  />
                }
                label={<Typography variant="caption">{ct.crop_type}</Typography>}
                sx={{ m: 0 }}
              />
            </Grid>
          ))}
          {filtered.length === 0 && (
            <Grid item xs={2}>
              <Typography variant="caption" color="text.secondary" sx={{ p: 1 }}>No results</Typography>
            </Grid>
          )}
        </Grid>
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function ChemicalsPage() {
  const navigate = useNavigate();
  const { session } = useAuth();
  const api = useAdminApi();
  const { viewer, openViewer, closeViewer } = useSdsDocuments();
  const [country, setCountry] = useCatalogueCountry();
  const countries = useCountries();

  // ---- Lookup data ----
  const [categories,        setCategories]        = useState<readonly CategoryOption[]>([]);
  const [fertCompanies,     setFertCompanies]      = useState<readonly CompanyOption[]>([]);
  const [cropTypes,         setCropTypes]          = useState<readonly CropTypeOption[]>([]);
  const [plantNutrients,    setPlantNutrients]     = useState<readonly PlantNutrient[]>([]);

  useEffect(() => {
    void (async () => {
      const result = await api.get<{
        categories: CategoryOption[];
        fertilzerCompanies: CompanyOption[];
        cropTypes: CropTypeOption[];
        plantNutrients: PlantNutrient[];
      }>(`/api/v1/admin/chemicals/lookup-data?country=${country}`);
      
      if (result.success) {
        const d = result.data;
        setCategories(d.categories ?? []);
        setFertCompanies(d.fertilzerCompanies ?? []);
        // Deduplicate crop types by name
        const seen = new Set<string>();
        const uniqueCropTypes = (d.cropTypes ?? []).filter((ct: CropTypeOption) => {
          const key = ct.crop_type.trim().toUpperCase();
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setCropTypes(uniqueCropTypes);
        setPlantNutrients(d.plantNutrients ?? []);
      }
    })();
  }, [api, country]);

  // ---- List state ----
  const [rows,         setRows]         = useState<readonly ChemicalRow[]>([]);
  const [total,        setTotal]        = useState(0);
  const [page,         setPage]         = useState(0);
  const [rowsPerPage,  setRowsPerPage]  = useState(10);
  const [search,       setSearch]       = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setPage(0); setDebouncedSearch(search); }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const [loading,      setLoading]      = useState(false);
  const [listError,    setListError]    = useState<string | null>(null);

  // ---- Dialog ----
  const [dialogOpen,   setDialogOpen]   = useState(false);
  const [editingId,    setEditingId]    = useState<number | null>(null);
  const [auditInfo,    setAuditInfo]    = useState<{
    created_at: string | null; updated_at: string | null;
    created_by: string | null; updated_by: string | null;
  } | null>(null);
  const [form,         setForm]         = useState<FormState>(DEFAULT_FORM);
  const [formError,    setFormError]    = useState<string | null>(null);
  const [saving,       setSaving]       = useState(false);

  // ---- Delete confirm ----
  const [deleteTarget, setDeleteTarget] = useState<ChemicalRow | null>(null);
  const [deleting,     setDeleting]     = useState(false);

  // ---- Link-check action busy flag (per-dialog) ----
  const [linkActionBusy, setLinkActionBusy] = useState(false);

  // ---- Snackbar ----
  const [snack,  setSnack]  = useState<{ msg: string; severity: 'success' | 'error' } | null>(null);

  const showSnack = useCallback(
    (msg: string, severity: 'success' | 'error' = 'success') => setSnack({ msg, severity }),
    [],
  );

  // ---- Load list ----
  const loadList = useCallback(async () => {
    setLoading(true);
    setListError(null);
    try {
      const result = await api.get<{ total: number; data: ChemicalRow[] }>(
        `/api/v1/admin/chemicals?page=${page + 1}&limit=${rowsPerPage}&country=${country}${debouncedSearch ? `&search=${encodeURIComponent(debouncedSearch)}` : ''}`
      );
      
      if (result.success) {
        setRows(result.data.data ?? []);
        setTotal(result.data.total ?? 0);
      } else {
        setListError(result.error.message);
      }
    } finally {
      setLoading(false);
    }
  }, [api, page, rowsPerPage, debouncedSearch, country]);

  useEffect(() => { void loadList(); }, [loadList]);

  // ---- Build default plant_nutrients dict ----
  const buildEmptyNutrients = useCallback(
    () => Object.fromEntries(plantNutrients.map((n) => [String(n.id), ''])),
    [plantNutrients],
  );

  // ---- Open add dialog ----
  const openAdd = () => {
    setEditingId(null);
    setAuditInfo(null);
    setFormError(null);
    setForm({ ...DEFAULT_FORM, plant_nutrients: buildEmptyNutrients(), country_code: country });
    setDialogOpen(true);
  };

  // ---- Open edit dialog ----
  const openEdit = async (row: ChemicalRow) => {
    setFormError(null);
    try {
      const result = await api.get<{ data: ChemicalRow }>(`/api/v1/admin/chemicals/${row.id}`);
      if (!result.success) throw result.error;

      const r = result.data.data;

      // Merge saved nutrients with all available nutrients (so new ones show empty)
      const defaultNutrients = buildEmptyNutrients();
      const savedNutrients   = (r.plant_nutrients as Record<string, string>) ?? {};
      const mergedNutrients  = { ...defaultNutrients, ...savedNutrients };

      setAuditInfo({
        created_at: r.created_at,
        updated_at: r.updated_at,
        created_by: r.created_by,
        updated_by: r.updated_by,
      });
      setForm({
        product:               r.product ?? '',
        category:              r.category ?? '',
        fertilzer_company:     r.fertilzer_company ?? '',
        rate:                  r.rate ?? '',
        cost:                  r.cost != null ? String(r.cost) : '',
        withhold_days:         r.withhold_days != null ? String(r.withhold_days) : '',
        safety_data_sheet_link: r.safety_data_sheet_link ?? '',
        chemical_label:        r.chemical_label ?? '',
        safety_data_sheets:    r.safety_data_sheets ?? '',
        viewUrl_sds:           r.safety_data_sheet_link_url ?? null,
        viewUrl_chem:          r.chemical_label_url ?? null,
        sds_link_status:           r.sds_link_status ?? null,
        sds_link_status_message:   r.sds_link_status_message ?? null,
        sds_link_replacement_url:  r.sds_link_replacement_url ?? null,
        sds_link_last_checked_at:  r.sds_link_last_checked_at ?? null,
        label_link_status:         r.label_link_status ?? null,
        label_link_status_message: r.label_link_status_message ?? null,
        label_link_replacement_url: r.label_link_replacement_url ?? null,
        label_link_last_checked_at: r.label_link_last_checked_at ?? null,
        crop_type_ids:         Array.isArray(r.crop_type) ? r.crop_type : [],
        plant_nutrients:       mergedNutrients,
        country_code:          toCatalogueCountry(r.country_code ?? row.country_code ?? country),
        mapp_number:           r.mapp_number ?? '',
        created_by:            r.created_by ?? '',
        updated_by:            r.updated_by ?? '',
      });
      setEditingId(row.id);
      setDialogOpen(true);
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Failed to load record', 'error');
    }
  };

  // ---- Save ----
  const handleSave = async () => {
    if (!form.product.trim()) {
      setFormError('Product name is required.');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const payload = {
        product:               form.product.trim(),
        category:              form.category !== '' ? form.category : null,
        fertilzer_company:     form.fertilzer_company !== '' ? form.fertilzer_company : null,
        rate:                  form.rate.trim() || null,
        cost:                  form.cost.trim() || null,
        withhold_days:         form.withhold_days.trim() || null,
        safety_data_sheet_link: form.safety_data_sheet_link.trim() || null,
        chemical_label:        form.chemical_label.trim() || null,
        safety_data_sheets:    form.safety_data_sheets !== '' ? form.safety_data_sheets : null,
        crop_type:             form.crop_type_ids,
        plant_nutrients:       form.plant_nutrients,
        country_code:          form.country_code,
        ...(form.country_code === 'GB' ? { mapp_number: form.mapp_number.trim() || null } : {}),
        created_by:            editingId ? form.created_by : (session?.user?.email ?? ''),
        updated_by:            session?.user?.email ?? '',
      };

      const result = editingId
        ? await api.put<{ data: ChemicalRow }>(`/api/v1/admin/chemicals/${editingId}`, payload)
        : await api.post<{ data: ChemicalRow }>('/api/v1/admin/chemicals', payload);

      if (!result.success) throw result.error;
      
      setDialogOpen(false);
      showSnack(editingId ? 'Chemical updated.' : 'Chemical added.');
      void loadList();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  // ---- Link-check actions ----
  const reloadEditingRow = useCallback(async () => {
    if (!editingId) return;
    const result = await api.get<{ data: ChemicalRow }>(`/api/v1/admin/chemicals/${editingId}`);
    if (!result.success) {
      showSnack(result.error.message, 'error');
      return;
    }
    const r = result.data.data;
    setForm((f) => ({
      ...f,
      safety_data_sheet_link:    r.safety_data_sheet_link ?? '',
      chemical_label:            r.chemical_label ?? '',
      sds_link_status:           r.sds_link_status ?? null,
      sds_link_status_message:   r.sds_link_status_message ?? null,
      sds_link_replacement_url:  r.sds_link_replacement_url ?? null,
      sds_link_last_checked_at:  r.sds_link_last_checked_at ?? null,
      label_link_status:         r.label_link_status ?? null,
      label_link_status_message: r.label_link_status_message ?? null,
      label_link_replacement_url: r.label_link_replacement_url ?? null,
      label_link_last_checked_at: r.label_link_last_checked_at ?? null,
    }));
  }, [api, editingId, showSnack]);

  const handleAcceptReplacement = useCallback(async (field: 'sds' | 'label') => {
    if (!editingId) return;
    setLinkActionBusy(true);
    try {
      const result = await api.post(`/api/v1/admin/chemicals/${editingId}/accept-replacement`, { field });
      if (!result.success) throw result.error;
      showSnack('Replacement link accepted.');
      await reloadEditingRow();
      void loadList();
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Failed to accept replacement', 'error');
    } finally {
      setLinkActionBusy(false);
    }
  }, [api, editingId, showSnack, reloadEditingRow, loadList]);

  const handleRejectReplacement = useCallback(async (field: 'sds' | 'label') => {
    if (!editingId) return;
    setLinkActionBusy(true);
    try {
      const result = await api.post(`/api/v1/admin/chemicals/${editingId}/reject-replacement`, { field });
      if (!result.success) throw result.error;
      showSnack('Replacement candidate dismissed.');
      await reloadEditingRow();
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Failed to reject replacement', 'error');
    } finally {
      setLinkActionBusy(false);
    }
  }, [api, editingId, showSnack, reloadEditingRow]);

  const handleRecheckLinks = useCallback(async () => {
    if (!editingId) return;
    setLinkActionBusy(true);
    try {
      const result = await api.post(`/api/v1/admin/chemicals/${editingId}/recheck-links`, {});
      if (!result.success) throw result.error;
      showSnack('Links re-checked.');
      await reloadEditingRow();
      void loadList();
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Recheck failed', 'error');
    } finally {
      setLinkActionBusy(false);
    }
  }, [api, editingId, showSnack, reloadEditingRow, loadList]);

  // ---- Delete ----
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const result = await api.delete(`/api/v1/admin/chemicals/${deleteTarget.id}`);
      if (!result.success) throw result.error;
      
      setDeleteTarget(null);
      showSnack('Chemical deleted.');
      void loadList();
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Delete failed.', 'error');
    } finally {
      setDeleting(false);
    }
  };


  const totalPages = Math.ceil(total / rowsPerPage);
  const isGb = country === 'GB';
  const formIsGb = form.country_code === 'GB';

  // ---- Category lookup map (ID string → name) ----
  const categoryLookup = useMemo(
    () => new Map(categories.map((c) => [String(c.id), c.category])),
    [categories],
  );

  // ---- Render ----
  return (
    <>
      <Container maxWidth="xl" sx={{ py: 4 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <Tooltip title="Back to Dashboard">
            <IconButton onClick={() => navigate('/dashboard')} size="small">
              <BackIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>Chemicals</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage chemical products, nutrients and crop type assignments
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Add Chemical
          </Button>
        </Box>

        {/* Toolbar */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <CatalogueCountrySwitch value={country} onChange={(c) => { setCountry(c); setPage(0); }} />
            <TextField
              size="small"
              placeholder="Search chemicals…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ width: 300 }}
            />
            {search && (
              <Button size="small" onClick={() => { setSearch(''); setPage(0); }}>
                Clear
              </Button>
            )}
            <Box sx={{ ml: 'auto' }}>
              <Chip label={`${total} records`} size="small" variant="outlined" />
            </Box>
          </Box>
        </Paper>

        {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>}

        {/* Table */}
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer>
            <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
              <AppTableHead>
                <TableRow>
                  <AppTableHeadCell>Product</AppTableHeadCell>
                  <AppTableHeadCell>Category</AppTableHeadCell>
                  {isGb ? <AppTableHeadCell>MAPP</AppTableHeadCell> : null}
                  <AppTableHeadCell align="right">Rate</AppTableHeadCell>
                  <AppTableHeadCell align="right">Cost</AppTableHeadCell>
                  <AppTableHeadCell align="center" sx={{ width: 380 }}>Link check</AppTableHeadCell>
                  <AppTableHeadCell align="center" sx={{ width: 88 }}>Links</AppTableHeadCell>
                  <AppTableHeadCell align="right" sx={{ width: 96 }}>Actions</AppTableHeadCell>
                </TableRow>
              </AppTableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={isGb ? 8 : 7} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isGb ? 8 : 7} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    No records found
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => (
                <AppTableRow key={row.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{row.product}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {row.category ? categoryLookup.get(row.category) ?? row.category : '—'}
                    </Typography>
                  </TableCell>
                  {isGb ? (
                    <TableCell>
                      <Typography variant="body2">{row.mapp_number?.trim() ? row.mapp_number : '—'}</Typography>
                    </TableCell>
                  ) : null}
                  <TableCell align="right">
                    <Typography variant="body2">{row.rate ?? '—'}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">
                      {row.cost != null ? `${catalogueCurrency(countries, country)}${Number(row.cost).toFixed(2)}` : '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                      <LinkStatusChip
                        label="SDS"
                        status={row.sds_link_status}
                        url={row.safety_data_sheet_link}
                        message={row.sds_link_status_message}
                      />
                      <LinkStatusChip
                        label="Label"
                        status={row.label_link_status}
                        url={row.chemical_label}
                        message={row.label_link_status_message}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      {row.chemical_label_url ? (
                        <Tooltip title="Chemical Label">
                          <IconButton size="small" onClick={() => openViewer(row.chemical_label_url, `${row.product} — Chemical Label`)}>
                            <DescriptionIcon fontSize="small" color="primary" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      {row.safety_data_sheet_link_url ? (
                        <Tooltip title="Safety Data Sheet">
                          <IconButton size="small" onClick={() => openViewer(row.safety_data_sheet_link_url, `${row.product} — Safety Data Sheet`)}>
                            <OpenInNewIcon fontSize="small" color="success" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                      {!row.chemical_label_url && !row.safety_data_sheet_link_url && (
                        <Typography variant="caption" color="text.secondary">—</Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => void openEdit(row)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => setDeleteTarget(row)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </AppTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

          {/* Pagination bar */}
          <Box sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: theme.palette.mode === 'light'
              ? alpha(theme.palette.primary.main, 0.09)
              : darken(theme.palette.background.paper, 0.35),
            px: 2,
            py: 1.5,
          })}>
            <Typography
              variant="body2"
              sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85 })}
            >
              {loading ? 'Loading…' : total === 0 ? 'No records found'
                : `Showing ${page * rowsPerPage + 1} to ${Math.min((page + 1) * rowsPerPage, total)} of ${total} entries`}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}
                >
                  Show
                </Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0); }}
                  sx={(theme) => ({
                    color: theme.palette.text.primary,
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider },
                    '& .MuiSvgIcon-root': { color: theme.palette.text.primary },
                    '& .MuiSelect-select': { py: 0.5, px: 1 },
                  })}
                >
                  {ROWS_PER_PAGE_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n}>{n}</MenuItem>
                  ))}
                </Select>
              </Box>

              {total > 0 && (
                <Typography
                  variant="body2"
                  sx={(theme) => ({ color: theme.palette.text.primary, opacity: 0.85, whiteSpace: 'nowrap' })}
                >
                  Page {page + 1} of {totalPages}
                </Typography>
              )}

              {totalPages > 1 && (
                <Pagination
                  count={totalPages}
                  page={page + 1}
                  onChange={(_, p) => setPage(p - 1)}
                  color="primary"
                  size="small"
                  disabled={loading}
                  sx={(theme) => ({
                    '& .MuiPaginationItem-root': {
                      color: theme.palette.text.primary,
                      borderColor: theme.palette.divider,
                      '&:hover': { backgroundColor: theme.palette.action.hover },
                    },
                    '& .Mui-selected': {
                      backgroundColor: `${theme.palette.primary.main} !important`,
                      color: theme.palette.primary.contrastText,
                    },
                    '& .Mui-disabled': { opacity: 0.5 },
                  })}
                />
              )}
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* ================================================================
          Add / Edit Dialog
          ================================================================ */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {editingId ? 'Edit Chemical' : 'Add Chemical'}
          {form.country_code !== 'AU' ? (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', fontWeight: 600 }}>
              {catalogueCountryName(countries, form.country_code)} catalogue
            </Typography>
          ) : null}
        </DialogTitle>

        <DialogContent dividers sx={{ pt: 2 }}>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

          <Grid container spacing={3}>
            {/* ---- Left column ---- */}
            <Grid item xs={12} md={6}>
              {/* Product */}
              <TextField
                label="Product"
                required
                fullWidth
                size="small"
                value={form.product}
                onChange={(e) => setForm((f) => ({ ...f, product: e.target.value }))}
                sx={{ mb: 2 }}
              />

              {/* Crop Types */}
              <CropTypeSelector
                cropTypes={cropTypes}
                selectedIds={form.crop_type_ids}
                onChange={(ids) => setForm((f) => ({ ...f, crop_type_ids: ids }))}
              />
            </Grid>

            {/* ---- Right column ---- */}
            <Grid item xs={12} md={6}>
              {/* Category */}
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  label="Category"
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value as string | '' }))}
                >
                  <MenuItem value=""><em>— Select —</em></MenuItem>
                  {categories.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>{c.category}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Cost */}
              <TextField
                label={`Cost (${catalogueCurrency(countries, form.country_code)})`}
                fullWidth
                size="small"
                type="number"
                inputProps={{ step: '0.01', min: 0 }}
                value={form.cost}
                onChange={(e) => setForm((f) => ({ ...f, cost: e.target.value }))}
                InputProps={{ startAdornment: <InputAdornment position="start">{catalogueCurrency(countries, form.country_code)}</InputAdornment> }}
                sx={{ mb: 2 }}
              />

              {/* Rate */}
              <TextField
                label="Rate"
                fullWidth
                size="small"
                type="number"
                inputProps={{ step: '0.01' }}
                value={form.rate}
                onChange={(e) => setForm((f) => ({ ...f, rate: e.target.value }))}
                sx={{ mb: 2 }}
              />

              {/* Withhold Days */}
              <TextField
                label={formIsGb ? 'Harvest interval (days)' : 'Withhold Days'}
                fullWidth
                size="small"
                type="number"
                inputProps={{ min: 0 }}
                value={form.withhold_days}
                onChange={(e) => setForm((f) => ({ ...f, withhold_days: e.target.value }))}
                sx={{ mb: 2 }}
              />

              {/* MAPP number (UK only) */}
              {formIsGb ? (
                <TextField
                  label="MAPP number"
                  fullWidth
                  size="small"
                  inputProps={{ maxLength: 20 }}
                  value={form.mapp_number}
                  onChange={(e) => setForm((f) => ({ ...f, mapp_number: e.target.value }))}
                  sx={{ mb: 2 }}
                />
              ) : null}

              {/* Fertilizer Company */}
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Fertilizer Company</InputLabel>
                <Select
                  label="Fertilizer Company"
                  value={form.fertilzer_company}
                  onChange={(e) => setForm((f) => ({ ...f, fertilzer_company: e.target.value as string | '' }))}
                >
                  <MenuItem value=""><em>— Select —</em></MenuItem>
                  {fertCompanies.map((c) => (
                    <MenuItem key={c.id} value={String(c.id)}>{c.company}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Safety Data Sheet Link */}
              <Box sx={{ mb: 2 }}>
                {editingId && (
                  <LinkCheckBanner
                    status={form.sds_link_status}
                    message={form.sds_link_status_message}
                    replacementUrl={form.sds_link_replacement_url}
                    lastCheckedAt={form.sds_link_last_checked_at}
                    onAccept={() => handleAcceptReplacement('sds')}
                    onReject={() => handleRejectReplacement('sds')}
                    onRecheck={() => handleRecheckLinks()}
                    busy={linkActionBusy}
                  />
                )}
                <DocField
                  label="Safety Data Sheet Link"
                  urlValue={form.safety_data_sheet_link}
                  viewUrl={form.viewUrl_sds}
                  onUrlChange={(v) => setForm((f) => ({ ...f, safety_data_sheet_link: v }))}
                  onOpen={openViewer}
                />
              </Box>

              {/* Chemical Label */}
              <Box sx={{ mb: 2 }}>
                {editingId && (
                  <LinkCheckBanner
                    status={form.label_link_status}
                    message={form.label_link_status_message}
                    replacementUrl={form.label_link_replacement_url}
                    lastCheckedAt={form.label_link_last_checked_at}
                    onAccept={() => handleAcceptReplacement('label')}
                    onReject={() => handleRejectReplacement('label')}
                    onRecheck={() => handleRecheckLinks()}
                    busy={linkActionBusy}
                  />
                )}
                <DocField
                  label="Chemical Label"
                  urlValue={form.chemical_label}
                  viewUrl={form.viewUrl_chem}
                  onUrlChange={(v) => setForm((f) => ({ ...f, chemical_label: v }))}
                  onOpen={openViewer}
                />
              </Box>
            </Grid>

            {/* ---- Full-width: Plant Nutrients ---- */}
            <Grid item xs={12}>
              <Divider sx={{ mb: 2 }}>
                <Chip label="Plant Nutrients" size="small" />
              </Divider>
              <Grid container spacing={2}>
                {plantNutrients.map((n) => (
                  <Grid key={n.id} item xs={12} sm={6} md={4} lg={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          minWidth: 36,
                          height: 36,
                          borderRadius: 1,
                          bgcolor: n.background_color,
                          color: n.text_color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          fontSize: 12,
                          flexShrink: 0,
                        }}
                      >
                        {n.symbol}
                      </Box>
                      <TextField
                        label={n.element}
                        size="small"
                        type="number"
                        fullWidth
                        inputProps={{ step: '0.01' }}
                        value={form.plant_nutrients[String(n.id)] ?? ''}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            plant_nutrients: { ...f.plant_nutrients, [String(n.id)]: e.target.value },
                          }))
                        }
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            {/* ---- Audit Trail (edit mode only) ---- */}
            {editingId && auditInfo && (
              <Grid item xs={12}>
                <Box
                  p={2}
                  sx={{ bgcolor: (t) => alpha(t.palette.primary.main, 0.12), borderRadius: 1 }}
                >
                  <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={1} sx={{ textTransform: 'uppercase', letterSpacing: 0.8, textAlign: 'center' }}>
                    Audit Trail
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.disabled" display="block">Created</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fmtAuDate(auditInfo.created_at)}{auditInfo.created_by ? <> &mdash; <strong>{auditInfo.created_by}</strong></> : ''}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.disabled" display="block">Updated</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {fmtAuDate(auditInfo.updated_at)}{auditInfo.updated_by ? <> &mdash; <strong>{auditInfo.updated_by}</strong></> : ''}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} color="inherit">Cancel</Button>
          <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
            {saving ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
            {editingId ? 'Save Changes' : 'Add Chemical'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================================================================
          Delete confirm dialog
          ================================================================ */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs">
        <DialogTitle>Delete Chemical</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>{deleteTarget?.product}</strong>? This cannot be undone and may affect
            records that reference this chemical.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleDelete()}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} /> : undefined}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={Boolean(snack)}
        autoHideDuration={3500}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack(null)}
          severity={snack?.severity ?? 'success'}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack?.msg}
        </Alert>
      </Snackbar>

      <SdsDocumentViewerDialog viewer={viewer} onClose={closeViewer} />
    </>
  );
}
